import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { getEvents, type AccessEvent } from "../../lib/api";

function timeAgo(ts: number, lang: "ru" | "uk"): string {
  const d = new Date(ts * 1000);
  return d.toLocaleString(lang === "uk" ? "uk-UA" : "ru-RU", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

function nameOf(e: AccessEvent, t: (k: string) => string): string {
  if (!e.user_id) return t("logs_unknown_user");
  const parts = [e.first_name, e.last_name].filter(Boolean).join(" ").trim();
  return parts || e.user_phone || `#${e.user_id}`;
}

function metaTgId(meta: string | null): string | null {
  if (!meta) return null;
  try {
    const obj = JSON.parse(meta);
    if (typeof obj.telegram_user_id === "number") return String(obj.telegram_user_id);
  } catch {}
  return null;
}

export function LogsTab() {
  const { t, lang } = useAuth();
  const [events, setEvents] = useState<AccessEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    setLoading(true); setError(null);
    try {
      const { events } = await getEvents(100);
      setEvents(events);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); }, []);

  return (
    <section className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-800">{t("logs_title")}</div>
        <button onClick={reload} className="text-xs text-indigo-600 font-semibold">{loading ? "…" : t("logs_refresh")}</button>
      </div>
      {error && <div className="text-xs text-rose-600">{error}</div>}
      {!loading && events.length === 0 && <div className="text-xs text-slate-500">{t("logs_empty")}</div>}
      {events.length > 0 && (
        <ul className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
          {events.map(e => {
            const isOk = e.event_type === "login_ok";
            const tgId = metaTgId(e.meta);
            return (
              <li key={e.id} className="p-3 flex items-start justify-between gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${isOk ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                      {isOk ? t("logs_login_ok") : t("logs_login_denied")}
                    </span>
                    {e.role === "admin" && <span className="text-[10px] text-indigo-600 font-semibold">admin</span>}
                  </div>
                  <div className="font-medium text-slate-900 mt-0.5 truncate">{nameOf(e, t)}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {e.user_phone ?? (tgId ? `tg:${tgId}` : "")}
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 shrink-0">{timeAgo(e.at, lang)}</div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
